/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import type { ProductFormData, ReviewCandidate } from '../../interface/types';
import { useEffect, useMemo, useState } from 'react';
import { useInitiateSubmitMutation } from '@/features/customReviews/api/postInitiateSubmit';
import { getFingerprint } from '@/features/reviews/utils/getFingerprint';
import { useLocale } from '@/hooks/useLocale';
import { usePageType } from '@/hooks/usePageType';
import { type SubmitReviewPageType } from '../../../reviews/interface';
import logger from '@/utils/logger';
import { clickTrack } from '@/utils/analytics/clickTrack';
import root from 'window-or-global';
import { normalizeErrorForLogging } from '@/utils/errorsHandling/normalizeErrorForLogging';
import { ActionCandidatesProvider } from '@/components/ActionCandidates/context/ActionCandidatesProvider';
import { ActionCandidates } from '@/components/ActionCandidates';

type ReviewsContainerProps = {
  orders: ReviewCandidate[];
  label: string;
  disableClose?: boolean;
  limit?: number;
  onClose: () => void;
};

export const ReviewCandidates = ({
  orders,
  label,
  disableClose,
  limit,
  onClose,
}: ReviewsContainerProps) => {
  const [latestProducts, setLatestProducts] = useState<ReviewCandidate[]>(orders.slice(0, limit));
  const [productFormData, setProductFormData] = useState<ProductFormData | undefined>();
  const isProductCountOverLimit = !!limit && orders.length > limit;
  const locale = useLocale();
  const firstUAS = useMemo(() => latestProducts[0]?.uas, [latestProducts]);
  const pageType = usePageType().pageType as SubmitReviewPageType | undefined;

  const { mutateAsync: getReviewsProgress, isPending: isInitiationPending } =
    useInitiateSubmitMutation({
      onSuccess: ({ data }) => {
        if (data.response?.productFormData) {
          setProductFormData(data.response.productFormData);
        }
      },
    });

  useEffect(() => {
    if (!firstUAS || !pageType || latestProducts.length === 0) {
      return;
    }
    const productIds = latestProducts.map((product) => product.sku);
    const fp = getFingerprint();
    void getReviewsProgress({
      productIds,
      locale,
      pageType,
      fp,
      userToken: firstUAS,
    }).catch((error) => {
      logger.error({
        message: 'Failed to get review form data',
        meta: {
          source: 'ReviewCandidates',
          error: normalizeErrorForLogging(error),
        },
      });
    });
  }, [firstUAS, getReviewsProgress, latestProducts, locale, pageType]);

  if (latestProducts.length === 0) {
    return null;
  }

  const isOneProduct = latestProducts.length === 1;

  const handleClose = () => {
    const { pageType: analyticsPageType, pageName, siteSection } = root.utag_data ?? {};
    clickTrack({
      eventType: 'closeReviewsContainer',
      customLinkName: `reviewsContainerClosed-${analyticsPageType}`,
      pageName,
      pageType: analyticsPageType,
      siteSection,
    });
    onClose();
  };

  return (
    <ActionCandidatesProvider
      reviewCandidates={latestProducts}
      setLatestProducts={setLatestProducts}
      formData={productFormData}
      isInitiationPending={isInitiationPending}
      isOneProduct={isOneProduct}
    >
      <ActionCandidates
        label={label}
        disableClose={disableClose}
        isProductCountOverLimit={isProductCountOverLimit}
        handleClose={handleClose}
      />
    </ActionCandidatesProvider>
  );
};
